import { fetchAuthSession } from 'aws-amplify/auth'
//import authService from '../services/authService'

export interface SessionInfo {
  isValid: boolean
  expiresAt?: Date
  timeUntilExpiry?: number
  needsRefresh: boolean
}

class SessionManager {
  private refreshTimer: number | null = null
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000 // 5 minutes before expiry
  private readonly CHECK_INTERVAL = 60 * 1000 // Check every minute

  /**
   * Start automatic session monitoring and refresh
   */
  startSessionMonitoring(): void {
    this.stopSessionMonitoring()
    
    this.refreshTimer = window.setInterval(async () => {
      try {
        const sessionInfo = await this.getSessionInfo()
        
        if (sessionInfo.needsRefresh) {
          await this.refreshSession()
        }
      } catch (error) {
        console.warn('Session monitoring error:', error)
      }
    }, this.CHECK_INTERVAL)
  }

  /**
   * Stop session monitoring
   */
  stopSessionMonitoring(): void {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /**
   * Get current session information
   */
  async getSessionInfo(): Promise<SessionInfo> {
    try {
      const session = await fetchAuthSession()
      
      if (!session.tokens?.accessToken) {
        return {
          isValid: false,
          needsRefresh: false
        }
      }

      const accessToken = session.tokens.accessToken
      const payload = this.parseJwtPayload(accessToken.toString())
      
      if (!payload || !payload.exp) {
        return {
          isValid: false,
          needsRefresh: false
        }
      }

      const expiresAt = new Date(payload.exp * 1000)
      const now = new Date()
      const timeUntilExpiry = expiresAt.getTime() - now.getTime()
      
      return {
        isValid: timeUntilExpiry > 0,
        expiresAt,
        timeUntilExpiry,
        needsRefresh: timeUntilExpiry > 0 && timeUntilExpiry < this.REFRESH_THRESHOLD
      }
    } catch (error) {
      return {
        isValid: false,
        needsRefresh: false
      }
    }
  }

  /**
   * Manually refresh the session
   */
  async refreshSession(): Promise<boolean> {
    try {
      await fetchAuthSession({ forceRefresh: true })
      return true
    } catch (error) {
      console.error('Session refresh failed:', error)
      return false
    }
  }

  /**
   * Check if session is valid and not expired
   */
  async isSessionValid(): Promise<boolean> {
    const sessionInfo = await this.getSessionInfo()
    return sessionInfo.isValid
  }

  /**
   * Get time until session expires (in milliseconds)
   */
  async getTimeUntilExpiry(): Promise<number | null> {
    const sessionInfo = await this.getSessionInfo()
    return sessionInfo.timeUntilExpiry || null
  }

  /**
   * Parse JWT payload without verification (for expiry checking)
   */
  private parseJwtPayload(token: string): { exp?: number } | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded) as { exp?: number }
    } catch (error) {
      return null
    }
  }

  /**
   * Format time until expiry as human readable string
   */
  formatTimeUntilExpiry(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / (1000 * 60))
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`
    } else {
      return 'Less than a minute'
    }
  }

  /**
   * Add session expiry warning callback
   */
  onSessionExpiring(callback: (timeUntilExpiry: number) => void, threshold: number = this.REFRESH_THRESHOLD): void {
    const checkExpiry = async () => {
      const sessionInfo = await this.getSessionInfo()
      
      if (sessionInfo.isValid && sessionInfo.timeUntilExpiry && sessionInfo.timeUntilExpiry < threshold) {
        callback(sessionInfo.timeUntilExpiry)
      }
    }

    // Check immediately and then periodically
    checkExpiry()
    window.setInterval(checkExpiry, 30000) // Check every 30 seconds
  }
}

// Export singleton instance
export const sessionManager = new SessionManager()
export default sessionManager